import React from 'react';
import PropTypes from 'prop-types';
import { compose, withHandlers, lifecycle, withState } from 'recompose';
import throttle from 'lodash/throttle';
import {
  DialogContainer,
  Dialog,
  Overlay,
  Grid,
  Direction,
  Column,
  SROnly,
  IconClose,
  WithConditionalWrap
} from '@scotia/canvas-react';
import './ScotiawebModal.scss';
import { Portal } from '../Portal/Portal';

const container = (window && window.parent.document) || document;
/** ID for style tag placed on parent document taken from iframe */
const sCRstyleTagId = 'scr-iframe-styles';
/** The ID of the iframe where is placed the MFEs, CAREFUL if it is changed on SPA */
const mfeIframeID = 'iframe-stage';

const mountStylesToParent = () => {
  try {
    removeStylesFromParent();
    const iframeStyles = container
      .getElementById(mfeIframeID)
      .contentWindow.document.getElementsByTagName('style');
    let stringStyles = '';
    for (let i = 0; i < iframeStyles.length; i++) {
      stringStyles = stringStyles + ' ' + iframeStyles[i].innerHTML;
    }
    let style = container.createElement('style');
    style.type = 'text/css';
    style.id = sCRstyleTagId;
    if (style.styleSheet) {
      style.styleSheet.cssText = stringStyles;
    } else {
      style.appendChild(container.createTextNode(stringStyles));
    }
    let head = container.head || container.getElementsByTagName('head')[0];
    head.appendChild(style);
  } catch (e) {}
};

const removeStylesFromParent = () => {
  try {
    container.getElementById(sCRstyleTagId).remove();
  } catch (e) {}
};

const Button = props => (
  <button {...props} className={`button ${props.className}`}>
    {props.children}
  </button>
);

const DefaultLayout = ({ children }) => (
  <Grid center className="xs-no-margin">
    <Direction type="row" className="block--centered xs-no-margin">
      <Column xs={12} sm={10} md={8} lg={8} className="xs-no-margin">
        {children}
      </Column>
    </Direction>
  </Grid>
);

DefaultLayout.propTypes = {
  children: PropTypes.node.isRequired
};

const DialogContainerWrapper = ({ children, Layout }) => (
  <DialogContainer
    key="dialog"
    role="dialog"
    aria-modal="true"
    style={{ zIndex: 10, height: 'auto' }}
  >
    <Layout>{children}</Layout>
  </DialogContainer>
);

DialogContainerWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  Layout: PropTypes.func.isRequired
};

const renderCloseButton = onClose => (
  <Button className="dialog__button-close" onClick={onClose}>
    <SROnly>Close</SROnly>
    <IconClose color="default" />
  </Button>
);

const WrappedModal = ({
  isOpen,
  children,
  onKeyDown,
  onClose,
  showButtonClose,
  dialogClassName,
  isXsWidth,
  Layout
}) => {
  const view = isOpen && (
    <Portal>
      <div id={'canvas-modal'} className="canvas" key={'wrappedModalKey'}>
        <WithConditionalWrap
          key="dialog-container"
          condition={!isXsWidth}
          wrap={DialogContainerWrapper}
          Layout={Layout}
        >
          <Dialog
            onKeyDown={onKeyDown}
            tabIndex="0"
            className={dialogClassName}
            style={{ zIndex: 10, height: 'auto' }}
          >
            {showButtonClose && onClose && renderCloseButton(onClose)}
            {children}
          </Dialog>
        </WithConditionalWrap>
        <Overlay key="overlay" tabIndex="-1" style={{ zIndex: 9 }} />
      </div>
    </Portal>
  );

  return view;
};

WrappedModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  children: PropTypes.node,
  onClose: PropTypes.func,
  showButtonClose: PropTypes.bool,
  Layout: PropTypes.func,
  dialogClassName: PropTypes.string
};

WrappedModal.defaultProps = {
  onClose: null,
  showButtonClose: false,
  Layout: DefaultLayout
};

const getFocusableElements = () =>
  container
    .querySelector('.dialog')
    .querySelectorAll(
      'a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]'
    );

const adjustModalMargin = () => {
  const containerCenter = container.querySelector(
    '.canvas .container.container--center'
  );
  containerCenter && (containerCenter.style.margin = '0 auto');
};

const adjustModalScroll = () => {
  if (window.innerWidth < xsWidth) {
    const dialog = container.querySelector('.dialog');
    if (dialog) {
      container.querySelector('.dialog').style.top = '0px';
      container.documentElement.scrollTop = 0;
    }
  }
};

const setAriaModalOpen = () => {
  mountStylesToParent();
  const firstElem = container.body.firstElementChild;
  firstElem.setAttribute('aria-hidden', 'true');
  firstElem.setAttribute('tabIndex', -1);
  firstElem.classList.add('scroll--disabled');
  container.body.classList.add('modal-open');
  const focusableElements = getFocusableElements();
  if (focusableElements.length > 0) focusableElements[0].focus();
};

const setAriaModalClosed = focusedElementBeforeOpen => {
  removeStylesFromParent();
  const firstElem = container.body.firstElementChild;
  firstElem.removeAttribute('aria-hidden');
  firstElem.removeAttribute('tabIndex');
  firstElem.classList.remove('scroll--disabled');
  container.body.classList.remove('modal-open');
  if (focusedElementBeforeOpen) focusedElementBeforeOpen.focus();
};

const handleModalKeyDown = (onClose, event) => {
  const focusableElements = getFocusableElements();
  const head = focusableElements[0];
  const tail = focusableElements[focusableElements.length - 1];
  const TAB_KEY = 9;
  const ESC_KEY = 27;
  let target;
  switch (event.keyCode) {
    case TAB_KEY:
      if (focusableElements.length === 1) {
        event.preventDefault();
        break;
      }
      if (head === container.activeElement && event.shiftKey) {
        target = tail;
      }
      if (tail === container.activeElement && !event.shiftKey) {
        target = head;
      }
      if (target) {
        event.preventDefault();
        target.focus();
      }
      break;
    case ESC_KEY:
      onClose();
      break;
    default:
      break;
  }
};

const xsWidth = 768;
const throttledHandleWindowResize = setIsXsWidth =>
  throttle(() => {
    setIsXsWidth(window.innerWidth < xsWidth);
  }, 20);

const ModalWithState = compose(
  withState('focusedElementBeforeOpen', 'setFocusedElementBeforeOpen', ''),
  withState(
    'isXsWidth',
    'setIsXsWidth',
    () =>
      typeof window !== 'undefined' &&
      window.innerWidth &&
      window.innerWidth < xsWidth
  ),
  lifecycle({
    componentWillReceiveProps(nextProps) {
      if (!this.props.isOpen && nextProps.isOpen) {
        this.props.setFocusedElementBeforeOpen(container.activeElement);
      }
    },
    componentDidUpdate(prevProps) {
      adjustModalMargin();
      adjustModalScroll();
      if (!prevProps.isOpen && this.props.isOpen) {
        setAriaModalOpen();
      } else if (prevProps.isOpen && !this.props.isOpen) {
        setAriaModalClosed(this.props.focusedElementBeforeOpen);
      }
    },
    componentWillUnmount() {
      window.removeEventListener(
        'resize',
        throttledHandleWindowResize(this.props.setIsXsWidth)
      );
      if (this.props.focusedElementBeforeOpen) {
        setAriaModalClosed(this.props.focusedElementBeforeOpen);
      }
    },
    componentDidMount() {
      window.addEventListener(
        'resize',
        throttledHandleWindowResize(this.props.setIsXsWidth)
      );
    }
  }),
  withHandlers({
    onKeyDown: ({ onClose }) => event => handleModalKeyDown(onClose, event)
  })
);

export const ScotiawebModal = ModalWithState(WrappedModal);
